import { Component, computed, inject, signal } from '@angular/core';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwKbd } from '../../shared/ui/kbd/uw-kbd';
import { CommandService } from '../../core/services/command.service';
import type { CommandItem } from '../../core/models/command.model';

@Component({
  selector: 'uw-command-palette',
  standalone: true,
  imports: [UwIcon, UwKbd],
  template: `
    @if (command.open()) {
      <div class="fixed inset-0 z-modal flex items-start justify-center pt-[12vh]" role="dialog" aria-modal="true" aria-label="Command palette">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" (click)="command.closePalette()" aria-hidden="true"></div>

        <div
          class="relative w-[560px] max-w-[calc(100vw-32px)] overflow-hidden rounded-xl border border-line bg-surface-elevated shadow-lg"
          (keydown.escape)="command.closePalette()"
        >
          <div class="flex items-center gap-2 border-b border-line px-3.5 py-3">
            <uw-icon name="search" [size]="16" class="shrink-0 text-text-muted" />
            <input
              #input
              class="w-full bg-transparent text-[13px] text-primary placeholder:text-text-muted focus:outline-none"
              placeholder="Search commands, accounts, actions…"
              [value]="command.query()"
              (input)="command.query.set(input.value)"
              (keydown)="onKeydown($event)"
              aria-label="Search commands"
            />
            <uw-kbd>Esc</uw-kbd>
          </div>

          <div class="max-h-[50vh] overflow-y-auto p-1.5">
            @if (!command.query()) {
              <p class="typo-label px-3 pb-1 pt-2">Recent</p>
              @if (command.recent().length === 0) {
                <p class="px-3 py-2 text-[12px] text-text-muted">No recent commands. Run something with ⌘K / Ctrl+K.</p>
              } @else {
                @for (id of command.recent(); track id) {
                  @if (command.commands(); as commands) {
                    @if (commands.find((c) => c.id === id); as cmd) {
                      <button
                        class="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-sunken"
                        (click)="run(cmd)"
                      >
                        <uw-icon [name]="cmd.icon" [size]="15" class="text-text-muted" />
                        <span class="flex-1 truncate text-[12.5px] text-primary">{{ cmd.label }}</span>
                      </button>
                    }
                  }
                }
              }
            }

            @for (group of groups(); track group.name) {
              <p class="typo-label px-3 pb-1 pt-2">{{ group.name }}</p>
              @for (item of group.items; track item.id; let i = $index) {
                <button
                  class="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors"
                  [class.bg-ai-soft]="indexOf(item.id) === activeIndex()"
                  [class.hover:bg-surface-sunken]="indexOf(item.id) !== activeIndex()"
                  (mouseenter)="command.activeIndex.set(indexOf(item.id))"
                  (click)="run(item)"
                >
                  <uw-icon [name]="item.icon" [size]="15" class="text-text-muted" />
                  <span class="flex-1 truncate text-[12.5px] text-primary">{{ item.label }}</span>
                  @if (item.shortcut) {
                    <span class="text-[10.5px] font-semibold text-text-muted">{{ item.shortcut }}</span>
                  }
                </button>
              }
            }

            @if (command.filtered().length === 0) {
              <div class="px-3 py-6 text-center">
                <p class="text-[12.5px] font-semibold text-primary">No results for "{{ command.query() }}"</p>
                <p class="mt-0.5 text-[11.5px] text-text-muted">Try "high priority", "ferris", "pricing", or "theme".</p>
              </div>
            }
          </div>

          <div class="flex items-center gap-3 border-t border-line bg-surface-sunken/50 px-3.5 py-2 text-[10.5px] text-text-muted">
            <span class="flex items-center gap-1"><uw-kbd>↑</uw-kbd><uw-kbd>↓</uw-kbd> navigate</span>
            <span class="flex items-center gap-1"><uw-kbd>↵</uw-kbd> run</span>
            <span class="ml-auto">UR Copilot</span>
          </div>
        </div>
      </div>
    }
  `,
})
export class UwCommandPalette {
  protected readonly command = inject(CommandService);
  protected readonly activeIndex = computed(() => this.command.activeIndex());

  protected readonly groups = computed(() => {
    const items = this.command.filtered();
    const order = ['Navigate', 'Accounts', 'Copilot', 'Actions', 'Preferences'];
    return order
      .map((name) => ({ name, items: items.filter((i) => i.group === name) }))
      .filter((g) => g.items.length > 0);
  });

  protected indexOf(id: string): number {
    return this.command.filtered().findIndex((i) => i.id === id);
  }

  protected run(item: CommandItem): void {
    this.command.run(item);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.command.moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.command.moveActive(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.command.selectActive();
    }
  }
}
